import React, { useEffect, useState } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Loader2, CreditCard, Check } from 'lucide-react';
import { getAuthHeaders } from '@/hooks/useAuth';

export type SavedCard = { id: string; brand: string; last4: string; expMonth: number; expYear: number };

// One Stripe instance for the whole app. loadStripe injects a script tag, so calling
// it per render would add one on every mount.
let stripePromise: Promise<Stripe | null> | null = null;
const getStripe = (key: string) => {
  if (!stripePromise) stripePromise = loadStripe(key);
  return stripePromise;
};

const cardLabel = (c: SavedCard) =>
  `${c.brand ? c.brand[0].toUpperCase() + c.brand.slice(1) : 'Card'} ····${c.last4}`;

// The form itself. Lives inside <Elements> so it can reach the SetupIntent.
function CardForm({ onSaved, onCancel }: { onSaved: (c: SavedCard) => void; onCancel: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setError(null);
    // No redirect: this is a card being filed, not a payment, so there is nowhere
    // meaningful to send them and a round trip would lose the booking they were on.
    const { error: err, setupIntent } = await stripe.confirmSetup({ elements, redirect: 'if_required' });
    if (err) { setError(err.message || 'That card could not be saved.'); setBusy(false); return; }
    const pmId = typeof setupIntent?.payment_method === 'string'
      ? setupIntent.payment_method
      : setupIntent?.payment_method?.id;
    if (!pmId) { setError('That card could not be saved.'); setBusy(false); return; }
    // Make it the default so the charge path has exactly one card to reach for.
    try {
      const res = await fetch('/api/billing/payment-method/default', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ paymentMethodId: pmId }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not save your card');
      const { card } = await res.json();
      onSaved(card);
    } catch (e: any) {
      setError(e.message || 'Could not save your card');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit}>
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'hsl(var(--destructive))' }}>{error}</p>}
      <div className="flex items-center gap-2" style={{ marginTop: 12 }}>
        <button type="submit" disabled={!stripe || busy}
          style={{ background: 'var(--hj-yellow)', color: '#0A0A0A', border: 'none', borderRadius: 6, padding: '10px 18px', fontWeight: 700, fontSize: 14, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Save card'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}
          style={{ background: 'transparent', border: '1px solid var(--hj-hairline)', borderRadius: 6, padding: '10px 18px', fontSize: 14, cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
      <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--hj-ink-2)' }}>
        We save it now and only charge it when a reader accepts your session. Nothing is
        taken while you're waiting.
      </p>
    </form>
  );
}

/**
 * Card on file. Shows the saved card, or the form to add one.
 *
 * Reader requests used to run Stripe Checkout per booking, which meant money moved
 * before anyone had agreed to anything. With a card filed here, asking a reader
 * costs nothing and the charge happens the moment one of them accepts.
 */
export function CardOnFile({ onCardChange }: { onCardChange?: (c: SavedCard | null) => void }) {
  const [card, setCard] = useState<SavedCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [pubKey, setPubKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await fetch('/api/billing/payment-method', { headers: getAuthHeaders() });
      const d = res.ok ? await res.json() : { card: null };
      setCard(d.card || null);
      onCardChange?.(d.card || null);
    } catch { /* a card we can't read is the same as none for the caller */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const startAdd = async () => {
    setError(null);
    try {
      const res = await fetch('/api/billing/setup-intent', { method: 'POST', headers: getAuthHeaders() });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not start card setup');
      const d = await res.json();
      if (!d.publishableKey) throw new Error('Card payments are not configured.');
      setClientSecret(d.clientSecret); setPubKey(d.publishableKey); setAdding(true);
    } catch (e: any) { setError(e.message); }
  };

  const remove = async () => {
    if (!card) return;
    await fetch(`/api/billing/payment-method/${card.id}`, { method: 'DELETE', headers: getAuthHeaders() }).catch(() => {});
    setCard(null); onCardChange?.(null);
  };

  if (loading) return <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--hj-ink-3)' }} />;

  if (adding && clientSecret && pubKey) {
    return (
      <Elements stripe={getStripe(pubKey)} options={{ clientSecret, appearance: { theme: 'flat' } }}>
        <CardForm
          onSaved={(c) => { setCard(c); onCardChange?.(c); setAdding(false); }}
          onCancel={() => setAdding(false)}
        />
      </Elements>
    );
  }

  if (card) {
    return (
      <div className="flex items-center justify-between gap-3" style={{ padding: '10px 12px', border: '1px solid var(--hj-hairline)', borderRadius: 8 }}>
        <span className="flex items-center gap-2" style={{ fontSize: 13.5, color: 'var(--hj-ink)' }}>
          <Check className="w-4 h-4" style={{ color: 'var(--hj-green)' }} />
          {cardLabel(card)} <span style={{ color: 'var(--hj-ink-2)' }}>&middot; expires {String(card.expMonth).padStart(2, '0')}/{String(card.expYear).slice(-2)}</span>
        </span>
        <button type="button" onClick={remove}
          style={{ background: 'transparent', border: 'none', color: 'var(--hj-ink-2)', fontSize: 12.5, textDecoration: 'underline', cursor: 'pointer' }}>
          Remove
        </button>
      </div>
    );
  }

  return (
    <div>
      <button type="button" onClick={startAdd}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--hj-yellow)', color: '#0A0A0A', border: 'none', borderRadius: 6, padding: '10px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
        <CreditCard className="w-4 h-4" /> Add a card
      </button>
      {error && <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'hsl(var(--destructive))' }}>{error}</p>}
      <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--hj-ink-2)' }}>
        Asking a reader costs nothing. We only charge when one of them accepts.
      </p>
    </div>
  );
}
